/**
 * @ipc/admin-ui · shared/api/tenant-store — выбранный тенант (slug) для X-Tenant-Id.
 *
 * Раньше тенант был зашит в билд (VITE_TENANT_ID). Теперь он выбирается в UI
 * (переключатель в профиль-меню) и хранится в localStorage — переключение без
 * пересборки. api-client читает getTenantId() в заголовок X-Tenant-Id на admin-путях.
 *
 * Реактивность для React — через подписку (useSyncExternalStore в useTenantId).
 * Смена тенанта = смена физической БД на бэке, поэтому вызывающий сбрасывает
 * кэш TanStack Query (см. tenant-switch).
 */
import { TENANT_ID as DEFAULT_TENANT_ID } from '@/shared/config/env';

const TENANT_KEY = 'ipc-tenant-id';

type Listener = () => void;
const listeners = new Set<Listener>();

/** Текущий выбранный slug тенанта. Фолбэк — VITE_TENANT_ID (или 'default'). */
export function getTenantId(): string {
  try {
    const stored = localStorage.getItem(TENANT_KEY);
    if (stored !== null && stored.length > 0) {
      return stored;
    }
  } catch {
    // localStorage недоступен — используем сборочный дефолт.
  }
  return DEFAULT_TENANT_ID;
}

/** Устанавливает активный тенант и уведомляет подписчиков. */
export function setTenantId(slug: string): void {
  try {
    localStorage.setItem(TENANT_KEY, slug);
  } catch {
    // no-op — проживёт до перезагрузки.
  }
  for (const listener of listeners) {
    listener();
  }
}

/** Подписка на смену тенанта (для useSyncExternalStore). Возвращает отписку. */
export function subscribeTenant(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
